/**
 * The one mutable layer: signals holding the loaded file, the op list + cursor, and the current
 * selection, with computed signals deriving the current EditState → materialised FitFile →
 * Activity → stats/records. Because the view reads the *materialised* activity, the numbers on
 * screen are exactly what an export would contain. Nothing below this module imports from here.
 */
import { batch, computed, signal, type ReadonlySignal } from '@preact/signals'
import { decode } from '../codec/decode'
import { FitError } from '../codec/errors'
import { buildActivity, type Activity } from '../domain/model'
import { buildEditState, type EditState } from '../domain/edit'
import { materialize } from '../domain/materialize'
import { computeStats, type ActivityStats } from '../analytics/stats'
import { computeRecords, type SwimRecord } from '../analytics/records'
import { applyOp, fold, type Op } from './ops'

export interface Store {
  readonly fileName: ReadonlySignal<string | null>
  readonly error: ReadonlySignal<string | null>
  readonly hasActivity: ReadonlySignal<boolean>
  readonly activity: ReadonlySignal<Activity | null>
  readonly stats: ReadonlySignal<ActivityStats | null>
  readonly records: ReadonlySignal<readonly SwimRecord[]>
  readonly poolLengthCm: ReadonlySignal<number | null>
  readonly selection: ReadonlySignal<ReadonlySet<number>>
  readonly canUndo: ReadonlySignal<boolean>
  readonly canRedo: ReadonlySignal<boolean>
  readonly canReset: ReadonlySignal<boolean>

  loadFile(bytes: Uint8Array, fileName: string): boolean
  clear(): void
  clearError(): void

  merge(ordinals: number[]): OpResult
  split(ordinal: number, parts: number): OpResult
  setStroke(ordinals: number[], value: number): OpResult
  setPool(cm: number): OpResult
  undo(): void
  redo(): void
  reset(): void

  /** Selection mutation — all clicks route through these so the shift-anchor stays coherent. */
  select(ordinal: number, mods?: { shift?: boolean; toggle?: boolean }): void
  setSelection(ordinals: Iterable<number>): void
  clearSelection(): void

  /** Internal seams the export module needs. */
  readonly originalFile: ReadonlySignal<ReturnType<typeof decode> | null>
  readonly currentState: ReadonlySignal<EditState | null>
}

export interface OpResult {
  readonly ok: boolean
  readonly error?: string
}

function friendlyError(e: unknown): string {
  if (e instanceof FitError) return e.message
  return e instanceof Error ? e.message : String(e)
}

export function createStore(): Store {
  const fileName = signal<string | null>(null)
  const initialState = signal<EditState | null>(null)
  const ops = signal<Op[]>([])
  const cursor = signal(0)
  const selection = signal<ReadonlySet<number>>(new Set())
  const anchor = signal<number | null>(null) // last plain/toggle click, for shift-range
  const error = signal<string | null>(null)

  const currentState = computed<EditState | null>(() => {
    const init = initialState.value
    return init ? fold(init, ops.value.slice(0, cursor.value)) : null
  })
  const originalFile = computed(() => initialState.value?.originalFile ?? null)
  const activity = computed<Activity | null>(() => {
    const cur = currentState.value
    return cur ? buildActivity(materialize(cur)) : null
  })
  const stats = computed<ActivityStats | null>(() => {
    const a = activity.value
    return a ? computeStats(a) : null
  })
  const records = computed<readonly SwimRecord[]>(() => {
    const a = activity.value
    return a ? computeRecords(a) : []
  })
  const poolLengthCm = computed<number | null>(() => currentState.value?.poolLengthCm ?? null)
  const hasActivity = computed(() => initialState.value !== null)
  const canUndo = computed(() => cursor.value > 0)
  const canRedo = computed(() => cursor.value < ops.value.length)
  const canReset = computed(() => ops.value.length > 0)

  function loadFile(bytes: Uint8Array, name: string): boolean {
    try {
      const init = buildEditState(buildActivity(decode(bytes)))
      batch(() => {
        fileName.value = name
        initialState.value = init
        ops.value = []
        cursor.value = 0
        selection.value = new Set()
        anchor.value = null
        error.value = null
      })
      return true
    } catch (e) {
      error.value = friendlyError(e)
      return false
    }
  }

  function clear(): void {
    batch(() => {
      fileName.value = null
      initialState.value = null
      ops.value = []
      cursor.value = 0
      selection.value = new Set()
      anchor.value = null
      error.value = null
    })
  }

  function reset(): void {
    if (ops.value.length === 0) return
    batch(() => {
      ops.value = []
      cursor.value = 0
      selection.value = new Set()
      anchor.value = null
    })
  }

  function select(ordinal: number, mods: { shift?: boolean; toggle?: boolean } = {}): void {
    if (mods.shift && anchor.value !== null) {
      const lo = Math.min(anchor.value, ordinal)
      const hi = Math.max(anchor.value, ordinal)
      const range = new Set<number>()
      for (let o = lo; o <= hi; o++) range.add(o)
      selection.value = range // anchor unchanged, so the range can be re-dragged
      return
    }
    if (mods.toggle) {
      const next = new Set(selection.value)
      if (next.has(ordinal)) next.delete(ordinal)
      else next.add(ordinal)
      batch(() => {
        selection.value = next
        anchor.value = ordinal
      })
      return
    }
    batch(() => {
      selection.value = new Set([ordinal])
      anchor.value = ordinal
    })
  }

  function setSelection(ordinals: Iterable<number>): void {
    const set = new Set(ordinals)
    batch(() => {
      selection.value = set
      anchor.value = set.size > 0 ? Math.min(...set) : null
    })
  }

  function clearSelection(): void {
    batch(() => {
      selection.value = new Set()
      anchor.value = null
    })
  }

  function pushOp(op: Op, keepSelection: boolean): OpResult {
    const cur = currentState.value
    if (!cur) return { ok: false, error: 'no activity loaded' }
    try {
      applyOp(cur, op) // validate preconditions before committing
    } catch (e) {
      return { ok: false, error: friendlyError(e) }
    }
    batch(() => {
      ops.value = [...ops.value.slice(0, cursor.value), op]
      cursor.value = cursor.value + 1
      if (!keepSelection) {
        selection.value = new Set()
        anchor.value = null
      }
    })
    return { ok: true }
  }

  return {
    fileName,
    error,
    hasActivity,
    activity,
    stats,
    records,
    poolLengthCm,
    selection,
    canUndo,
    canRedo,
    originalFile,
    currentState,

    loadFile,
    clear,
    clearError: () => {
      error.value = null
    },

    merge: (ordinals) => pushOp({ type: 'merge', ordinals }, false),
    split: (ordinal, parts) => pushOp({ type: 'split', ordinal, parts }, false),
    setStroke: (ordinals, value) => pushOp({ type: 'setStroke', ordinals, value }, true),
    setPool: (cm) => pushOp({ type: 'setPool', cm }, true),
    undo: () => {
      if (cursor.value > 0) {
        batch(() => {
          cursor.value = cursor.value - 1
          selection.value = new Set()
          anchor.value = null
        })
      }
    },
    redo: () => {
      if (cursor.value < ops.value.length) {
        batch(() => {
          cursor.value = cursor.value + 1
          selection.value = new Set()
          anchor.value = null
        })
      }
    },
    reset,
    canReset,
    select,
    setSelection,
    clearSelection,
  }
}

/** The app-wide singleton. Tests use `createStore()` for isolation. */
export const store = createStore()
